// Tenant Contact API - API integration for searching tenant contacts
// Uses ApiClient with dependency injection pattern

import type { ApiClient } from '@commander/api-client';

// Backend response type (snake_case from API)
interface BackendTenantContactSearchResult {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  title?: string;
  tenant_company_id?: string;
}

// Frontend type for search results (simplified for autocomplete)
export interface TenantContactSearchResult {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  displayName: string; // Computed: "First Last" or email if no name
}

// Transform backend response to frontend type
function transformSearchResult(
  backend: BackendTenantContactSearchResult
): TenantContactSearchResult {
  const firstName = backend.first_name;
  const lastName = backend.last_name;

  // Compute display name: "First Last", "First", "Last", or email as fallback
  let displayName = '';
  if (firstName && lastName) {
    displayName = `${firstName} ${lastName}`;
  } else if (firstName) {
    displayName = firstName;
  } else if (lastName) {
    displayName = lastName;
  } else {
    displayName = backend.email;
  }

  return {
    id: backend.id,
    firstName,
    lastName,
    email: backend.email,
    displayName,
  };
}

/**
 * Factory function to create tenant contact API service with ApiClient dependency injection
 * @param apiClient - ApiClient instance from context
 * @returns Service object with tenant contact search method
 */
export const createTenantContactApi = (apiClient: ApiClient) => ({
  /**
   * Search tenant contacts by keyword (name or email)
   * GET /tenant_contacts/search
   * @param keyword - Search term (searches first_name, last_name, email)
   * @param limit - Maximum results to return (default 10, max 50)
   * @param masterCompanyLocationId - Optional location filter to scope contacts by location
   */
  async searchTenantContacts(
    keyword: string,
    limit = 10,
    masterCompanyLocationId?: string
  ): Promise<TenantContactSearchResult[]> {
    const params = new URLSearchParams();
    params.append('keyword', keyword);
    params.append('limit', limit.toString());
    if (masterCompanyLocationId) {
      params.append('master_company_location_id', masterCompanyLocationId);
    }

    const response = await apiClient.get<{ data: BackendTenantContactSearchResult[] }>(
      `/tenant_contacts/search?${params}`
    );

    return response.data.map(transformSearchResult);
  },
});

// Type for the service instance
export type TenantContactApi = ReturnType<typeof createTenantContactApi>;
